import {FC, useEffect, useState} from "react";
import Card from "../ui/Card";
import useApi from "../../hooks/useApi";
import Table from "../ui/Table";
import {FaPen, FaTrash} from "react-icons/fa";
import {toast} from "react-hot-toast";
import Loading from "../Loading";
import {AnimatePresence} from "framer-motion";
import Modal from "../ui/Modal";
import Button from "../ui/Button";
import axios from "axios";
import handleToastError from "../../helpers/handleToastError";

interface Props {
    spaceId: string
}

const SpaceDetailsMemberList: FC<Props> = (props) => {
    const {fetchSpaceAssignedMembers, deleteMemberOrAssignment} = useApi();
    const [loading, setLoading] = useState(false);
    const [spaceAssignedUsers, setSpaceAssignedUsers] = useState<{displayName: string, id: string, userId: string}[]>([]);
    const [spaceCurrentUsers, setSpaceCurrentUsers] = useState<{displayName: string, id: string}[]>([]);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [confirmationModalMode, setConfirmationModalMode] = useState<'member'|'assignment'>('member');
    const [deletionIds, setDeletionIds] = useState<[string,string]>(['','']);

    useEffect(() => {
        setLoading(true);
        if(props.spaceId) {
            fetchSpaceAssignedMembers(props.spaceId).then(res => {
                if(res) {
                    setSpaceAssignedUsers(res.assigned.map(x => ({
                        displayName: x.target.displayName,
                        id: x.id,
                        userId: x.target.objectId
                    })));
                    setSpaceCurrentUsers(res.current.map(x => ({
                        displayName: x.displayName,
                        id: x.id
                    })));
                }
            }).catch(err => {
                setSpaceCurrentUsers([]);
                setSpaceAssignedUsers([]);
                toast.error('Die Benutzer des Spaces konnten nicht abgeholt werden 😔');
            }).finally(() => setLoading(false));
        }
    },[fetchSpaceAssignedMembers,props.spaceId]);

    const onDeleteWithAccessPackage = async () => {
        let assignmentId;
        if(deletionIds[0].length > 0) assignmentId = deletionIds[0];
        await toast.promise(deleteMemberOrAssignment(props.spaceId, deletionIds[1], assignmentId),{
            loading: 'Das Assignment und der Nutzer werden entfernt',
            success: () => {
                setSpaceCurrentUsers(prev => prev.filter(x => x.id !== deletionIds[1]));
                setSpaceAssignedUsers(prev => prev.filter(x => x.id !== deletionIds[0]));
                return <span className='text-sm'>Das Assignment und der Nutzer wurden gelöscht</span>
            },
            error: (err) => handleToastError(err, "Das Assignment und/oder der Nutzer konnten nicht gelöscht werden")
        });
        setDeletionIds(['','']);
    }

    const onDeleteWithoutAccessPackage = () => {

    }

    return (
        <>
            <Card title='Zugewiesene Nutzer'>
                {!loading && (<Table.Skeleton>
                    <Table.THead>
                        <Table.HeadTR>
                            <Table.TH>Assignment ID</Table.TH>
                            <Table.TH>Name</Table.TH>
                        </Table.HeadTR>
                    </Table.THead>
                    <Table.TBody>
                        {spaceAssignedUsers.length > 0 ? (
                            spaceAssignedUsers.map(x => (
                                <Table.BodyTR key={x.id}>
                                    <Table.TD>{x.userId}</Table.TD>
                                    <Table.TD className='flex justify-between items-center'>
                                        <span>{x.displayName}</span>
                                        <div className='flex gap-2'>
                                            <FaTrash className='cursor-pointer text-error-brighter' onClick={() => {
                                                setShowConfirmationModal(true);
                                                setConfirmationModalMode('assignment');
                                                setDeletionIds([x.id,x.userId]);
                                            }}/>
                                        </div>
                                    </Table.TD>
                                </Table.BodyTR>
                            ))
                        ) : (
                            <Table.BodyTR>
                                <Table.TD colspan={2}>Keine Nutzer haben den Zugriff beantragt</Table.TD>
                            </Table.BodyTR>
                        )}
                    </Table.TBody>
                </Table.Skeleton>)}
                {loading && <Loading small={true}/>}
            </Card>
            <Card title='Aktuelle Nutzer'>
                {!loading && (<Table.Skeleton>
                    <Table.THead>
                        <Table.HeadTR>
                            <Table.TH>User ID</Table.TH>
                            <Table.TH>Name</Table.TH>
                        </Table.HeadTR>
                    </Table.THead>
                    <Table.TBody>
                        {spaceCurrentUsers.length > 0 ? (
                            spaceCurrentUsers.map(x => (
                                <Table.BodyTR key={x.id}>
                                    <Table.TD>{x.id}</Table.TD>
                                    <Table.TD className='flex justify-between items-center'>
                                        <span>{x.displayName}</span>
                                        <div className='flex gap-2'>
                                            <FaTrash className='cursor-pointer text-error-brighter' onClick={() => {
                                                setShowConfirmationModal(true);
                                                setConfirmationModalMode('member');
                                                setDeletionIds([spaceAssignedUsers.find(y => y.userId === x.id)?.id ?? '',x.id]);
                                            }}/>
                                        </div>
                                    </Table.TD>
                                </Table.BodyTR>
                            ))
                        ) : (
                            <Table.BodyTR>
                                <Table.TD colspan={2}>Es gibt keine Nutzer in diesem Team</Table.TD>
                            </Table.BodyTR>
                        )}
                    </Table.TBody>
                </Table.Skeleton>)}
                {loading && <Loading small={true}/>}
            </Card>
            <AnimatePresence>
                {showConfirmationModal && <Modal isOpen={showConfirmationModal} title={confirmationModalMode==='assignment' ? 'Willst du wirklich den User von der Gruppe entfernen?' : 'Soll das Assignment auch gelöscht werden?'} onClose={() => {setShowConfirmationModal(false)}}>
                    {confirmationModalMode === 'member' && <p>
                        Nachdem der User von einer Gruppe entfernt wird, soll auch das Assignment gelöscht werden. Das Löschen des Assignments stellt sicher, dass der Benutzer keine Inhalte mehr ansehen darf.<br/>
                        Außerdem wird innerhalb der 24 Stunden der Status der Assignments in die Gruppen gespiegelt und der Benutzer wird dem Team erneut zugewiesen.
                    </p>}
                    <div className='flex items-center justify-end mt-2 gap-2'>
                        <Button className='bg-success-darker hover:shadow-success-darker/20' onClick={() => {
                            setShowConfirmationModal(false);
                            onDeleteWithAccessPackage();
                        }}>Ja</Button>
                        <Button className='bg-error-brighter hover:shadow-error-brighter/20' onClick={() => {
                            setShowConfirmationModal(false);
                            onDeleteWithoutAccessPackage();
                        }}>Nein</Button>
                        <Button onClick={() => {
                            setShowConfirmationModal(false);
                        }}>Abbrechen</Button>
                    </div>
                </Modal>}
            </AnimatePresence>
        </>
    );
};

export default SpaceDetailsMemberList;